/**
 * ehr#682 (PROGRESS-NOTE V141 V86 / V87): the note's Comment history, as the rows of its window:
 * every comment action kept on the note (body.reviewLog, and the reviewer's own not sent yet) and
 * the review's steps (sent for review, sent back, taken back, co-signed), newest first, each
 * "<who> <what happened>", with the comment's words. Plain functions, tested without the editor.
 */
import type { ClinicalReviewEventView, ReviewLogEntry } from '@ehr/shared';

/** One edit as the history words it (V141 edChangeText): replaced, added or deleted words. */
export function editText(h: { del: string; add: string }): string {
  const tidy = (t: string) => t.replace(/^[\s.,;:]+|[\s,;:]+$/g, '') || t.trim();
  const del = tidy(h.del);
  const add = tidy(h.add);
  if (del && add) return `replaced "${del}" with "${add}"`;
  if (add) return `added "${add}"`;
  return `deleted "${del}"`;
}

const quoted = (t: string | undefined) => (t ? `: "${t}"` : '');
const on = (s: string | undefined) => (s ? ` on ${s}` : '');

/** What a Comment history entry says after the person's name. */
export function logPhrase(e: Pick<ReviewLogEntry, 'kind' | 'section' | 'text'>): string {
  switch (e.kind) {
    case 'comment':
      return `commented${on(e.section)}${quoted(e.text)}`;
    case 'reply':
      return `replied${on(e.section)}${quoted(e.text)}`;
    case 'resolve':
      return `resolved a comment${on(e.section)}${quoted(e.text)}`;
    case 'reopen':
      return `reopened a comment${on(e.section)}${quoted(e.text)}`;
    case 'delete':
      return `deleted a comment${on(e.section)}${quoted(e.text)}`;
    case 'keep':
      return `kept a change${on(e.section)}${e.text ? `: ${e.text}` : ''}`;
    case 'revert':
      return `reverted a change${on(e.section)}${e.text ? `: ${e.text}` : ''}`;
  }
}

/** What a review step says after the person's name. */
export function eventPhrase(e: Pick<ClinicalReviewEventView, 'action' | 'round' | 'message'>) {
  switch (e.action) {
    case 'submit':
      return e.round > 1 ? 'resubmitted the note' : 'signed the note and sent it for review';
    case 'changes':
      return `requested changes and sent the note back${quoted(e.message ?? undefined)}`;
    case 'reply':
      return `replied${quoted(e.message ?? undefined)}`;
    case 'recall':
      return 'took the note back';
    case 'cosign':
      return 'co-signed the note';
    case 'sign':
      return 'signed the note';
  }
}

export interface HistoryRow {
  key: string;
  /** When (ISO). */
  at: string;
  who: string;
  what: string;
}

/**
 * The rows, newest first. An entry not kept yet (empty `at`) takes the time the editor noted for
 * it (`noted`), else now; the same entry kept and still noted shows once.
 */
export function historyRows(o: {
  log: readonly ReviewLogEntry[];
  events: readonly ClinicalReviewEventView[];
  name: (id: string) => string;
  me: string;
  noted?: Record<string, string>;
  now: string;
}): HistoryRow[] {
  const seen = new Set<string>();
  const rows: HistoryRow[] = [];
  for (const e of o.log) {
    if (seen.has(e.id)) continue;
    seen.add(e.id);
    rows.push({
      key: `l:${e.id}`,
      at: e.at || o.noted?.[e.id] || o.now,
      who: o.name(e.by || o.me),
      what: logPhrase(e),
    });
  }
  for (const e of o.events)
    rows.push({
      key: `e:${e.id}`,
      at: new Date(e.createdAt).toISOString(),
      who: o.name(e.actorId),
      what: eventPhrase(e),
    });
  return rows.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0));
}
